import styled from "styled-components";

import Panel from "../Panel";
import Skeleton from "../Skeleton";
import { Member, daysLeft } from "../../../utils/partnerMetrics";

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 0;
  border-top: 1px solid #f1ebe3;

  &:first-child {
    border-top: none;
  }
`;

const Name = styled.span`
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #2a241f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Badge = styled.span<{ $urgent: boolean }>`
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ $urgent }) => ($urgent ? "#b4472b" : "#6b6259")};
  flex-shrink: 0;
`;

const Empty = styled.div`
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 13.5px;
  color: #9a8e84;
  padding: 20px 0;
  text-align: center;
`;

const daysLabel = (n: number) => {
  if (n <= 0) return "Today";
  return `${n} day${n === 1 ? "" : "s"} left`;
};

interface MemberMiniListProps {
  title: string;
  members: Member[];
  loading?: boolean;
  emptyMessage: string;
  action?: React.ReactNode;
  limit?: number;
}

const MemberMiniList = ({
  title,
  members,
  loading,
  emptyMessage,
  action,
  limit = 6,
}: MemberMiniListProps) => {
  if (loading) {
    return (
      <Panel title={title} action={action}>
        <List>
          {Array.from({ length: 4 }).map((_, i) => (
            <Item key={i}>
              <Skeleton $w="55%" $h="14px" />
              <Skeleton $w="60px" $h="14px" />
            </Item>
          ))}
        </List>
      </Panel>
    );
  }

  const shown = members.slice(0, limit);

  return (
    <Panel title={title} action={action}>
      {shown.length === 0 ? (
        <Empty>{emptyMessage}</Empty>
      ) : (
        <List>
          {shown.map((m) => {
            const d = daysLeft(m.expiresAt);
            return (
              <Item key={m.membershipId}>
                <Name>
                  {m.firstName} {m.lastName}
                </Name>
                <Badge $urgent={!isNaN(d) && d <= 3}>
                  {isNaN(d) ? "—" : daysLabel(d)}
                </Badge>
              </Item>
            );
          })}
        </List>
      )}
    </Panel>
  );
};

export default MemberMiniList;
